import React, { useState, useEffect, useCallback } from "react";
import AppButton from "../components/common/AppButton";
import { useSelector, useDispatch } from "react-redux";
import { getGroupedCustomWordsAsync, saveCustomWordsBulkAsync} from "../redux/customWord";
import { Trash } from "lucide-react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
  Input,
} from "@heroui/react";

import { useFooter } from "../components/layout/FooterContext";
import AppButtonFooter from "../components/common/AppButtonFooter";

const Glossary = () => {
  const dispatch = useDispatch();
  const { groupedWords, loading } = useSelector((state) => state.customWords);
  const { setFooterContent, setTitle } = useFooter();

  const [glossaryByType, setGlossaryByType] = useState({});
  const [newType, setNewType] = useState("");

  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  // Fetch grouped words on first mount
  useEffect(() => {
    dispatch(getGroupedCustomWordsAsync());
  }, [dispatch]);

  // Initialize glossaryByType from groupedWords
  useEffect(() => {
    if (groupedWords && Object.keys(groupedWords).length > 0) {
      const initialState = {};
      for (const [type, words] of Object.entries(groupedWords)) {
        initialState[type] = Array.isArray(words) ? words.join("\n") : "";
      }
      setGlossaryByType(initialState);
    }
  }, [groupedWords]);

  const handleChange = (type) => (e) => {
    setGlossaryByType((prev) => ({
      ...prev,
      [type]: e.target.value,
    }));
  };

  const handleSave = useCallback(() => {
    const allWords = [];

    for (const [type, value] of Object.entries(glossaryByType)) {
      const words = value
        .split("\n")
        .map((w) => w.trim())
        .filter(Boolean);

      words.forEach((word) => {
        allWords.push({ word, type_custom_word: type });
      });
    }

    console.log("Dispatching save:", allWords);
    dispatch(saveCustomWordsBulkAsync({ words: allWords }));
  }, [glossaryByType, dispatch]);

  // Set save button in footer
  useEffect(() => {
    setFooterContent(
      <footer className="flex justify-end gap-2">
        <AppButtonFooter
          onClick={handleSave}
          width="w-[120px]"
          disabled={loading || Object.keys(glossaryByType).length === 0}
        >
          {loading ? "Saving..." : "Speichern"}
        </AppButtonFooter>
      </footer>
    );

    setTitle("Glossar");

    return () => setFooterContent(null);
  }, [handleSave, loading, glossaryByType]);

  const handleAddType = (onClose) => {
    const trimmed = newType.trim().toLowerCase();
    if (trimmed && !glossaryByType[trimmed]) {
      setGlossaryByType((prev) => ({ ...prev, [trimmed]: "" }));
    }
    setNewType("");
    onClose();
  };

  const handleDeleteType = (type) => {
    setGlossaryByType((prev) => {
      const updated = { ...prev };
      delete updated[type];
      return updated;
    });
  };

  const glossaryTypes = Object.keys(glossaryByType);

  return (
    <div className="h-[82vh] bg-transparent rounded-2xl px-4">
      <div className="flex flex-col lg:flex-row gap-8 h-[82vh]  bg-surface rounded-2xl p-3 overflow-scroll">
        {/* Left Panel */}
        <div className="lg:w-1/3 space-y-4">
          <div>
            <p className="text-sm text-default-500">
              Hier können Sie ein Glossar an Wörtern oder Phrasen ergänzen.
              Sie können das Glossar in Kategorien gliedern.
              Verwenden Sie pro Wort eine neue Zeile.
            </p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex-1 overflow-y-auto  space-y-6">
          {glossaryTypes.map((type) => (
            <div key={type} className="relative">
              <label className="block mb-2 text-sm font-medium text-default-600 capitalize">
                {type}
              </label>
              <div className="bg-white rounded-xl ps-3 pe-1 py-1">
                <textarea
                  rows={6}
                  className="w-full dark:border-default-100 focus:outline-none focus:ring-2 focus:ring-transparent resize-none"
                  spellCheck={false}
                  autoCorrect="off"
                  autoComplete="off"
                  placeholder={`Enter ${type} words, one per line`}
                  value={glossaryByType[type] || ""}
                  onChange={handleChange(type)}
                />
              </div>
              <button
                className="absolute -top-2 right-0 p-2  text-red-500 hover:text-red-700"
                onClick={() => handleDeleteType(type)}
                title="Delete Type"
              >
                <Trash size={16} />
              </button>
            </div>
          ))}

          <div className="flex gap-4 pb-3">
            <AppButton
              onClick={onOpen}
              variant="ghost"
              width="w-[120px]"
              rounded="rounded-[16px]"
            >
              Neue Kategorie
            </AppButton>
          </div>
        </div>
      </div>

      {/* Modal to add new type */}
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Neue Kategorie</ModalHeader>
              <ModalBody>
                <Input
                  label="Name"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                />
              </ModalBody>
              <ModalFooter>
                <AppButton onClick={() => handleAddType(onClose)} width="w-full">
                  Erstellen
                </AppButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      </div>
  );
};

export default Glossary;
